import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const FAQ = ({ onOpenContact }) => {
  const { t } = useLanguage();

  const faqs = [
    { id: 'faq-1', question: t('faq.q1'), answer: t('faq.a1') },
    { id: 'faq-2', question: t('faq.q2'), answer: t('faq.a2') },
    { id: 'faq-3', question: t('faq.q3'), answer: t('faq.a3') },
    { id: 'faq-4', question: t('faq.q4'), answer: t('faq.a4') },
    { id: 'faq-5', question: t('faq.q5'), answer: t('faq.a5') },
    { id: 'faq-6', question: t('faq.q6'), answer: t('faq.a6') },
    { id: 'faq-7', question: t('faq.q7'), answer: t('faq.a7') },
  ];

  return (
    <section 
      id="faq"
      data-testid="faq-section"
      className="py-20 md:py-28 bg-[#F8F9FA]"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#1A3A4A]/50 mb-4">
            {t('faq.subtitle')}
          </p>
          <h2 
            data-testid="faq-title"
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A3A4A] tracking-tight mb-6"
          >
            {t('faq.title')}
          </h2>
          <p className="font-body text-lg text-[#1A3A4A]/60 max-w-2xl mx-auto">
            {t('faq.description')}
          </p>
        </motion.div>

        {/* FAQ Accordion - Enhanced styling */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={faq.id}
                className="bg-white border border-[#E5E7EB] rounded-xl px-6 overflow-hidden data-[state=open]:border-l-[3px] data-[state=open]:border-l-[#60B5AF] data-[state=open]:border-t-[#60B5AF]/30 data-[state=open]:border-r-[#60B5AF]/30 data-[state=open]:border-b-[#60B5AF]/30 data-[state=open]:shadow-md transition-all duration-200"
                data-testid={faq.id}
              >
                <AccordionTrigger className="py-5 font-heading text-[17px] md:text-lg font-bold text-[#1A3A4A] hover:text-[#60B5AF] hover:no-underline text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 font-body text-[#1A3A4A]/70 leading-relaxed text-[15px]">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* CTA - Enhanced */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-12"
        >
          <p className="font-body text-[#1A3A4A]/50 mb-3">
            {t('faq.stillQuestions')}
          </p>
          <a 
            href="#"
            className="inline-flex items-center font-body font-semibold text-[#60B5AF] hover:text-[#1A3A4A] hover:underline underline-offset-4 transition-all"
            onClick={(e) => {
              e.preventDefault();
              onOpenContact();
            }}
          >
            {t('faq.getInTouch')}
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
